import { isEqual } from 'lodash';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { Actions } from '~/eds/src/components/Actions/Actions';
import { Box } from '~/eds/src/components/Box/Box';
import { ButtonGroup } from '~/eds/src/components/ButtonGroup/ButtonGroup';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Paginate } from '~/eds/src/components/Paginate/Paginate';
import { StatusType } from '~/eds/src/types/StatusType';
import { UserAction } from '~/eds/src/types/UserAction';
import { EntityType } from '~/enums/EntityType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useErrors } from '~/hooks/useErrors';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

import {
  FEEDBACK_MAX_LENGTH,
  MAX_TESTCASE_NUMBER,
  REVIEW_TEST_CASE_LABEL_WIDTH,
} from '../../constants';
import { FieldValue } from '../../fields/types';
import {
  getReviewPaginateTailTooltip,
  mapStatusToTestCaseOutcome,
  mapTestCaseOutcomeToStatus,
  testIsTestCaseEvaluating,
} from '../../fields/utils';
import { usePrefetchDocuments } from '../../hooks/usePrefetchDocuments';
import {
  PromptModelTestCase,
  TestCaseReviewStatus,
} from '../../types/deprecated';
import {
  extractDocumentVersionIds,
  findTestCaseByNumber,
  findTestCaseIndexByNumber,
  isTestCaseRated,
} from '../../utils/testcase';
import { ReviewGoldValueFeedback } from './ReviewGoldValueFeedback';

interface Props {
  testCase: PromptModelTestCase;
  totalCount: number;
  onUpdateTestCaseNumber: (updatedTestCaseNumber: number) => void;
  isLoading?: boolean;
  readOnly?: boolean;
  testCases?: PromptModelTestCase[];
}

// TODO: Split `ReviewActions` and `ReviewPaginate` into separate components
export const ReviewPaginate = ({
  isLoading: isPollingTestCase,
  readOnly,
  testCase,
  totalCount,
  onUpdateTestCaseNumber,
  testCases,
}: Props) => {
  const enableTableExtraction = useFlag(FlagType.CustomAiTableExtraction);
  const highlightMethod = useFlag(FlagType.CustomAiHighlightMethod);
  const isCustomAiAsyncTestCreationExecutionEnabled = useFlag(
    FlagType.CustomAiAsyncTestCreationExecution,
  );
  const { getSearchParam } = useRouting();
  const isTableModel =
    enableTableExtraction && getSearchParam('type') === 'table';

  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const { RETRY_ERROR } = useErrors();
  const VALUE_DOES_NOT_EXIST_TEXT = intl.formatMessage({
    id: 'ReviewPaginate.valueDoesNotExistText',
    defaultMessage: 'Value does not exist in the document.',
    description: 'Text indicating value does not exist in document',
  });

  const dispatch = useDispatch();
  const { toast } = useToast();
  const tableConfig = useSelector(
    slices.xRay.selectors.selectTableConfiguration,
  );
  const config = useSelector(slices.xRay.selectors.selectConfiguration);
  const field = isTableModel ? null : config.field;
  const query = isTableModel ? tableConfig.query : config.query;

  const [
    createTestCase,
    { isLoading: isCreatingTestCase, isError: isCreateTestCaseError },
  ] = api.endpoints.createPromptModelTestCase.useMutation();

  const [updateTestCase, { isLoading: isReviewingTestCase }] =
    api.endpoints.updatePromptModelTestCase.useMutation();

  const { data: docsInScope = 0, isFetching: isFetchingDocsInScope } =
    api.endpoints.getDocsInScope.useQuery(
      {
        entityId: isTableModel
          ? tableConfig.tableSpecState.key
          : String(field?.id), // skipped if field is null
        entityType: isTableModel
          ? EntityType.DocumentAttachedData
          : EntityType.Field,
        query,
      },
      { skip: !isTableModel && !field },
    );

  const documentViewer = useSelector(
    slices.xRay.selectors.selectDocumentViewer,
  );
  const { isFetching: isLoadingActiveDocument } =
    api.endpoints.resolveDocument.useQuery(
      {
        documentId: documentViewer.documentId,
        documentVersionId: documentViewer.documentVersionId,
        options: {
          autoLatestVersion: !isCustomAiAsyncTestCreationExecutionEnabled,
        },
      },
      { skip: !documentViewer.documentVersionId },
    );

  useEffect(() => {
    setGoldValue(testCase.goldValue);
    setFeedback(testCase.feedback ?? '');
  }, [testCase.number, testCase.goldValue, testCase.feedback]);

  const { prefetchDocuments } = usePrefetchDocuments();

  useEffect(() => {
    if (!isCustomAiAsyncTestCreationExecutionEnabled || !testCases) return;

    const currentIndex = findTestCaseIndexByNumber(testCases, testCase.number);

    const nextTestCases = testCases.slice(currentIndex + 1);
    const prefetchIds = extractDocumentVersionIds(nextTestCases);

    if (prefetchIds.length > 0) {
      prefetchDocuments(prefetchIds);
    }
  }, [
    isCustomAiAsyncTestCreationExecutionEnabled,
    testCase.number,
    testCases,
    prefetchDocuments,
  ]);

  const [goldValue, setGoldValue] = useState<Nullable<FieldValue>>(
    testCase.goldValue ?? null,
  );

  const [feedback, setFeedback] = useState<string>(testCase.feedback ?? '');

  const draftTestCase = {
    ...testCase,
    goldValue,
    feedback: feedback || null,
  };

  const status = mapTestCaseOutcomeToStatus(testCase);
  const isDangerOrWarningStatus = status === 'danger' || status === 'warning';

  const canCreateTestCase = Boolean(
    testCase.number === totalCount && testCase.number < MAX_TESTCASE_NUMBER,
  );

  const canCreateMoreTestCases = isCustomAiAsyncTestCreationExecutionEnabled
    ? Boolean(totalCount < docsInScope && totalCount < MAX_TESTCASE_NUMBER)
    : totalCount < MAX_TESTCASE_NUMBER;

  // Show "Next Document" button for incorrect outcomes since we create test cases in background
  // Old behavior was to only show when on last test case
  const enableNextAction = Boolean(
    (isCustomAiAsyncTestCreationExecutionEnabled ? true : canCreateTestCase) &&
    !isCreateTestCaseError &&
    !readOnly &&
    isDangerOrWarningStatus,
  );

  const hasTestCasesAhead = isCustomAiAsyncTestCreationExecutionEnabled
    ? testCase.number < totalCount
    : false;

  const isLoadingTestCase =
    isPollingTestCase ||
    isReviewingTestCase ||
    isFetchingDocsInScope ||
    (isCreatingTestCase && !hasTestCasesAhead);

  const isLoadingForNextAction = isCustomAiAsyncTestCreationExecutionEnabled
    ? isLoadingTestCase && !isCreatingTestCase
    : isLoadingTestCase;

  const hasNoGoldValue =
    !goldValue || goldValue.type === 'unknown' || goldValue.value === '';

  const disableNextAction = Boolean(
    isLoadingForNextAction ||
    (!isTableModel && hasNoGoldValue) ||
    (feedback && feedback.length > FEEDBACK_MAX_LENGTH),
  );

  const enableTooltip = Boolean(
    disableNextAction && !isLoadingTestCase && !readOnly,
  );

  const isTestCaseDirty = !isEqual(testCase, draftTestCase);
  const disablePagination =
    disableNextAction &&
    isDangerOrWarningStatus &&
    (enableNextAction ? isTestCaseDirty : true);

  const isIncorrectWithoutGoldValue = Boolean(
    !isTableModel && testCase.outcome === 'incorrect' && hasNoGoldValue,
  );

  const isEvaluatingTestCase = testIsTestCaseEvaluating(testCase);

  const disableActions = Boolean(
    readOnly ||
    isReviewingTestCase ||
    isEvaluatingTestCase ||
    isLoadingActiveDocument,
  );

  const actionTooltip = isLoadingActiveDocument
    ? intl.formatMessage({
        id: 'ReviewPaginate.loadingDocumentTooltip',
        defaultMessage: 'Loading document…',
        description:
          'Tooltip shown when document in left panel is still loading',
      })
    : isEvaluatingTestCase
      ? intl.formatMessage({
          id: 'ReviewPaginate.evaluatingResultsTooltip',
          defaultMessage: 'Evaluating results…',
          description: 'Tooltip shown when test case is being evaluated',
        })
      : undefined;

  const addTestCaseToStore = (newTestCase: unknown) => {
    const testCase = newTestCase as PromptModelTestCase;
    if (isTableModel) {
      dispatch(slices.xRay.actions.addTableTestCase(testCase));
    } else {
      dispatch(slices.xRay.actions.addTestCase(testCase));
    }
    if (isCustomAiAsyncTestCreationExecutionEnabled && testCase?.entity?.id) {
      prefetchDocuments([Number(testCase.entity.id)]);
    }
  };

  const handleNextTestCaseNavigation = (shouldAutoNavigate: boolean): void => {
    const nextTestCaseNumber = testCase.number + 1;
    const nextTestCaseExists = findTestCaseByNumber(
      testCases,
      nextTestCaseNumber,
    );

    if (nextTestCaseExists) {
      // Use existing test case and navigate
      if (shouldAutoNavigate) {
        onUpdateTestCaseNumber(nextTestCaseNumber);
      }
    } else if (canCreateMoreTestCases) {
      // Create new test case before navigation to prevent UI errors
      handleCreateTestCase(true, shouldAutoNavigate);
    } else {
      toast({
        message: intl.formatMessage({
          id: 'ReviewPaginate.maxTestCasesReached',
          defaultMessage: 'Maximum number of test cases reached',
          description:
            'Warning message when maximum test cases limit is reached',
        }),
        status: 'warning',
      });
    }
  };

  const handleCreateTestCase = (
    triggerExecution = false,
    autoNavigate = true,
  ) => {
    createTestCase({
      modelId: testCase.modelId,
      version: testCase.versionNumber,
      highlightMethod,
      triggerExecution,
    })
      .unwrap()
      .then((newTestCase) => {
        if (newTestCase) {
          addTestCaseToStore(newTestCase);
          if (autoNavigate) {
            const nextTestCaseNumber = testCase.number + 1;
            onUpdateTestCaseNumber(nextTestCaseNumber);
          }
        }
      })
      .catch((error) => {
        const message = error.response?.data.detail ?? RETRY_ERROR;
        switch (message) {
          case intl.formatMessage({
            id: 'ReviewPaginate.noMoreDocumentsMessage',
            defaultMessage:
              'No (more) documents were found in the model scope.',
            description: 'Error message when no more documents found',
          }):
            break; // swallow error (product requirement)
          default:
            toast({
              message,
              status: 'danger',
            });
        }
      });
  };

  const handleNextAction = (shouldCreateTestCase: boolean) => {
    let preparedGoldValue = goldValue;
    if (
      !isTableModel &&
      goldValue &&
      goldValue.highlights === undefined &&
      testCase.goldValue &&
      testCase.goldValue.highlights
    ) {
      // Keep the highlights in sync
      preparedGoldValue = {
        ...goldValue,
        highlights: testCase.goldValue.highlights,
      };
    }
    updateTestCase({
      modelId: testCase.modelId,
      version: testCase.versionNumber,
      highlightMethod,
      outcome: 'incorrect',
      testCaseNumber: testCase.number,
      goldValue: preparedGoldValue,
      feedback,
    })
      .unwrap()
      .then((updatedTestCase) => {
        if (updatedTestCase) {
          if (isTableModel) {
            dispatch(
              slices.xRay.actions.setUpdatedTableTestCase(updatedTestCase),
            );
          } else {
            dispatch(slices.xRay.actions.setUpdatedTestCase(updatedTestCase));
          }

          if (!isCustomAiAsyncTestCreationExecutionEnabled) {
            if (shouldCreateTestCase && canCreateTestCase) {
              handleCreateTestCase();
            }
          } else {
            handleNextTestCaseNavigation(true);
          }
        }
      })
      .catch(() => {
        toast({
          message: RETRY_ERROR,
          status: 'danger',
        });
      });
  };

  const handleUpdatePageIndex = (updatedPageIndex: number) => {
    if (!readOnly && isDangerOrWarningStatus && isTestCaseDirty) {
      handleNextAction(false);
    }
    onUpdateTestCaseNumber(updatedPageIndex);
  };

  const disabledControlsConfig = isCustomAiAsyncTestCreationExecutionEnabled
    ? {
        next: !isTestCaseRated(testCase) || isIncorrectWithoutGoldValue,
        last: (() => {
          if (!isTestCaseRated(testCase) || isIncorrectWithoutGoldValue) {
            return true;
          }

          // Enable if second-to-last test case is reviewed
          if (testCases && totalCount > 1) {
            const secondToLastTestCase = testCases.find(
              (tc) => tc.number === totalCount - 1,
            );
            if (isTestCaseRated(secondToLastTestCase)) {
              return false;
            }
          }

          return true;
        })(),
      }
    : undefined;

  const paginate = {
    disabled: disablePagination,
    disableGoTo: isCustomAiAsyncTestCreationExecutionEnabled || undefined,
    isLoading: isLoadingTestCase || isCreatingTestCase,
    loadingTooltip: isCreatingTestCase
      ? intl.formatMessage({
          id: 'ReviewPaginate.creatingNextTestCaseTooltip',
          defaultMessage: 'Creating next test case…',
          description:
            'Loading tooltip when creating next test case at runtime',
        })
      : intl.formatMessage({
          id: 'ReviewPaginate.analyzingNextDocumentTooltip',
          defaultMessage: 'Analyzing next document…',
          description: 'Loading tooltip for analyzing next document',
        }),
    nextAction: enableNextAction
      ? {
          text: intl.formatMessage({
            id: 'ReviewPaginate.nextDocumentText',
            defaultMessage: 'Next Document',
            description: 'Next document button text',
          }),
          disabled: disableNextAction,
          tooltip: enableTooltip
            ? intl.formatMessage(
                {
                  id: 'ReviewPaginate.provideValueTooltip',
                  defaultMessage: `Provide a value, or select '{valueDoesNotExistText}'`,
                  description:
                    'Tooltip instructing user to provide value. Example: valueDoesNotExistText = "Value does not exist"',
                },
                { valueDoesNotExistText: VALUE_DOES_NOT_EXIST_TEXT },
              )
            : undefined,
          onClick: () => handleNextAction(true),
        }
      : undefined,
    pageIndex: Math.min(testCase.number, totalCount),
    totalCount: isCreatingTestCase ? testCase.number : totalCount,
    getTooltips: ({ pageIndex }: { pageIndex: number }) => ({
      next: getReviewPaginateTailTooltip({
        docsInScope,
        pageIndex,
        intl,
        formatNumber,
      }),
      last: getReviewPaginateTailTooltip({
        docsInScope,
        pageIndex,
        intl,
        formatNumber,
      }),
    }),
    onUpdatePageIndex: handleUpdatePageIndex,
    ...(disabledControlsConfig && {
      disabledControls: disabledControlsConfig,
    }),
  };
  const infoMessage =
    disablePagination && !enableNextAction
      ? intl.formatMessage(
          {
            id: 'ReviewPaginate.provideValueInfoMessage',
            defaultMessage: `Provide a value, or select '{valueDoesNotExistText}'`,
            description:
              'Info message instructing user to provide value. Example: valueDoesNotExistText = "Not Found"',
          },
          { valueDoesNotExistText: VALUE_DOES_NOT_EXIST_TEXT },
        )
      : undefined;

  const reviewActions: UserAction[] = [
    {
      status: 'success',
      text: intl.formatMessage({
        id: 'ReviewPaginate.correctActionText',
        defaultMessage: 'Correct',
        description: 'Correct review action button text',
      }),
      tooltip: actionTooltip,
    },
    {
      status: testCase.outcome === 'partially_correct' ? 'warning' : 'danger',
      text: intl.formatMessage({
        id: 'ReviewPaginate.incorrectActionText',
        defaultMessage: 'Incorrect',
        description: 'Incorrect review action button text',
      }),
      tooltip: actionTooltip,
    },
    {
      status: 'inactive',
      text: intl.formatMessage({
        id: 'ReviewPaginate.skipActionText',
        defaultMessage: 'Skip',
        description: 'Skip review action button text',
      }),
      tooltip: actionTooltip,
    },
  ];

  const handleReviewDocument = (updatedStatus: TestCaseReviewStatus) => {
    if (readOnly) {
      return; // noop
    }

    const outcome = mapStatusToTestCaseOutcome(updatedStatus);

    let preparedGoldValue: Nullable<FieldValue> | undefined; // intentionally undefined unless there are gold value highlights
    if (goldValue?.highlights) {
      if (outcome === 'correct') {
        preparedGoldValue = {
          highlights: goldValue.highlights,
          type: testCase.modelValue?.type,
          // @ts-expect-error - 'string[]' is not assignable to 'string'
          value: testCase.modelValue?.value,
        };
      } else if (outcome === 'incorrect' && !config.fieldClassification) {
        // Setting null value and 'unknown' type on an incorrect extraction test case indicates that this is
        // a special case where the user modified the source highlights rather than the gold value itself.
        preparedGoldValue = {
          highlights: goldValue.highlights,
          type: 'unknown',
          value: null,
        };
      } else {
        preparedGoldValue = goldValue;
      }
    } else if (
      !goldValue &&
      outcome === 'incorrect' &&
      testCase.modelValue?.highlights
    ) {
      // Copy the model value highlights to the gold value highlights in order to
      // preserve them when the gold value gets set for the first time.
      preparedGoldValue = {
        highlights: testCase.modelValue.highlights,
        type: config.fieldClassification ? 'classification-unknown' : 'unknown',
        value: null,
      };
    }

    updateTestCase({
      modelId: testCase.modelId,
      version: testCase.versionNumber,
      highlightMethod,
      outcome,
      testCaseNumber: testCase.number,
      goldValue: isTableModel ? null : preparedGoldValue,
    })
      .unwrap()
      .then((updatedTestCase) => {
        if (updatedTestCase) {
          if (isTableModel) {
            dispatch(
              slices.xRay.actions.setUpdatedTableTestCase(updatedTestCase),
            );
          } else {
            dispatch(slices.xRay.actions.setUpdatedTestCase(updatedTestCase));
          }

          // Maintain buffer of 2 pre-created test cases after review
          if (isCustomAiAsyncTestCreationExecutionEnabled) {
            const shouldAutoNavigate =
              outcome === 'correct' || outcome === 'skipped';
            const hasNextTestCase = testCase.number < totalCount;
            if (hasNextTestCase || canCreateMoreTestCases) {
              handleNextTestCaseNavigation(shouldAutoNavigate);
            }
          }
        }
      })
      .catch(() => {
        toast({
          message: RETRY_ERROR,
          status: 'danger',
        });
      });
  };

  useEffect(() => {
    if (testCase && !isCustomAiAsyncTestCreationExecutionEnabled) {
      if (
        canCreateTestCase &&
        (testCase.outcome === 'correct' || testCase.outcome === 'skipped')
      ) {
        handleCreateTestCase();
      }
    }
  }, [
    testCase,
    isCustomAiAsyncTestCreationExecutionEnabled,
    canCreateTestCase,
  ]);

  return (
    <Layout align="flex-start" direction="column" pt={py} spacing={py}>
      <Layout spacing={2}>
        {/* Phantom Box for alignment */}
        <Box w={REVIEW_TEST_CASE_LABEL_WIDTH} />
        <ButtonGroup
          actions={reviewActions.map((reviewAction) => ({
            ...reviewAction,
            disabled: disableActions,
            icon: undefined, // never set an icon
            level: 'secondary' as const, // always use a secondary level action
            status:
              status === reviewAction.status
                ? (reviewAction.status as StatusType)
                : undefined,
            onClick: () =>
              handleReviewDocument(reviewAction.status as TestCaseReviewStatus),
          }))}
        />
      </Layout>
      {isDangerOrWarningStatus && (
        <ReviewGoldValueFeedback
          readOnly={readOnly}
          testCase={testCase}
          goldValue={goldValue}
          feedback={feedback}
          onUpdateGoldValue={setGoldValue}
          onUpdateFeedback={setFeedback}
        />
      )}
      <Layout spacing={2}>
        {/* Phantom Box for alignment */}
        <Box
          w={REVIEW_TEST_CASE_LABEL_WIDTH}
          ml={paginate.nextAction ? -16 : 0}
        />
        <Layout preset="buttons">
          <Paginate mode="count" name="review card paginate" {...paginate} />
          {infoMessage && <Icon icon="info" tooltip={infoMessage} />}
          {paginate.nextAction && (
            <Actions
              actions={[
                {
                  ...paginate.nextAction,
                  level: 'secondary',
                },
              ]}
            />
          )}
        </Layout>
      </Layout>
    </Layout>
  );
};

const py = 6;
